import "../styles/DashboardCollectivite.css";
import React from "react";
import { FaUser, FaUserMd, FaCalendarAlt } from "react-icons/fa";
import { API_BASE } from "../config";


/* =========================
   RDV + WORKFLOW 
========================= */
export function RendezVousCollectivite({ user }) {
  // -------------------------
  // Motifs annulation
  // -------------------------
  const MOTIFS_ANNULATION = [
    "Raison personnelle",
    "Raison professionnelle",
    "Raison familiale",
    "Raison logistique",
    "Autre raison",
  ];

  // -------------------------
  // Helpers
  // -------------------------
  const pad2 = React.useCallback((n) => String(n).padStart(2, "0"), []);

  const toISODate = React.useCallback(
    (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`,
    [pad2]
  );

  const fmtDateTime = React.useCallback((dt) => {
    if (!dt) return "—";
    const d = dt instanceof Date ? dt : new Date(dt);
    if (Number.isNaN(d.getTime())) return String(dt);
    return d.toLocaleString("fr-FR", {
      timeZone: "Europe/Paris"
    });
  }, []);

  const fmtTime = React.useCallback((isoOrDate) => {
    const d = isoOrDate instanceof Date ? isoOrDate : new Date(isoOrDate);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleTimeString("fr-FR", {
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "Europe/Paris"
    });
  }, []);

  const dayShort = React.useCallback(
    (d) => d.toLocaleDateString("fr-FR", { weekday: "short" }),
    []
  );

  const dayLong = React.useCallback(
    (d) =>
      d.toLocaleDateString("fr-FR", {
        weekday: "long",
        day: "numeric",
        month: "long",
      }),
    []
  );

  const monthLabel = React.useCallback(
    (d) => d.toLocaleDateString("fr-FR", { month: "long", year: "numeric" }),
    []
  );



  const startOfMonth = React.useCallback((d) => {
    const x = new Date(d);
    x.setDate(1);
    x.setHours(0, 0, 0, 0);
    return x;
  }, []);

  const endOfMonth = React.useCallback((d) => {
    const x = new Date(d);
    x.setMonth(x.getMonth() + 1);
    x.setDate(0);
    x.setHours(23, 59, 59, 999);
    return x;
  }, []);

  const addMonths = React.useCallback((d, n) => {
    const x = new Date(d);
    x.setDate(1);
    x.setMonth(x.getMonth() + n);
    x.setHours(0, 0, 0, 0);
    return x;
  }, []);

  // -------------------------
  // API + auth
  // -------------------------
  const authHeaders = React.useMemo(
    () => ({
      "Content-Type": "application/json",
      Authorization: `Bearer ${user?.token}`,
    }),
    [user?.token]
  );

  // -------------------------
  // Doctor formatting + avatar
  // -------------------------
  const doctorInitials = (m) => {
    const full = (m?.nom_complet || "").trim();
    if (!full) return "DR";
    const parts = full.split(/\s+/).filter(Boolean);
    const a = parts[0]?.[0] || "D";
    const b = parts[1]?.[0] || "R";
    return (a + b).toUpperCase();
  };

  const formatDoctorName = (m) => {
    if (!m) return "—";
    const name = (m.nom_complet || "").trim();
    if (!name) return "Dr —";
    if (/^dr\.?\s/i.test(name)) return name.replace(/^dr\.?\s/i, "Dr ");
    return `Dr ${name}`;
  };

  const doctorAddressLine = (m) => {
    const addr = (m?.adresse_cabinet || "").trim();
    return addr || "Adresse non renseignée";
  };

  const DoctorAvatar = ({ med }) => (
    <div className="rdv2DocAvatar" aria-hidden="true">
      <span className="rdv2DocInitials">{doctorInitials(med)}</span>
    </div>
  );

  // -------------------------
  // State: liste RDV
  // -------------------------
  const [rdvs, setRdvs] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");

  // -------------------------
  // State: workflow création RDV
  // -------------------------
  const [open, setOpen] = React.useState(false);
  const [step, setStep] = React.useState(1);

  const [beneficiaires, setBeneficiaires] = React.useState([]);
  const [motifs, setMotifs] = React.useState([]);
  const [medecins, setMedecins] = React.useState([]);
  const [creneaux, setCreneaux] = React.useState([]);

  const [selectedBenef, setSelectedBenef] = React.useState(null);
  const [selectedMotif, setSelectedMotif] = React.useState(null);
  const [selectedMed, setSelectedMed] = React.useState(null);
  const [selectedSlot, setSelectedSlot] = React.useState(null);

  const [wfLoading, setWfLoading] = React.useState(false);
  const [wfError, setWfError] = React.useState("");

  const [benefQuery, setBenefQuery] = React.useState("");
  const [monthCursor, setMonthCursor] = React.useState(() => startOfMonth(new Date()));
  const [selectedDayISO, setSelectedDayISO] = React.useState(null);

  // Step 5 recap
  const [successRecap, setSuccessRecap] = React.useState(null);

  // -------------------------
  // State: annulation RDV
  // -------------------------
  const [cancelModal, setCancelModal] = React.useState(null);
  const [confirmCancel, setConfirmCancel] = React.useState(false);
  const [detailModal, setDetailModal] = React.useState(null);

  // -------------------------
  // State: modification RDV
  // -------------------------
  const [modifModal, setModifModal] = React.useState(null);
  // null | { id, reference, medecin_nom, medecin_user_id, debut, beneficiaire_prenom, beneficiaire_nom }
  const [modifCreneaux, setModifCreneaux] = React.useState([]);
  const [modifMonthCursor, setModifMonthCursor] = React.useState(null);
  const [modifSelectedDay, setModifSelectedDay] = React.useState(null);
  const [modifSelectedSlot, setModifSelectedSlot] = React.useState(null);
  const [modifLoading, setModifLoading] = React.useState(false);
  const [modifError, setModifError] = React.useState("");
  const [q, setQ] = React.useState("");
  const qRef = React.useRef(q);
  React.useEffect(() => { qRef.current = q; }, [q]);
  const [page, setPage] = React.useState(1);
  const [total, setTotal] = React.useState(0);
  const limit = 7;

  const pages = Math.max(1, Math.ceil(total / limit));

  // Toast
  const [toast, setToast] = React.useState({ type: "", msg: "" });
  const showToast = (type, msg) => {
    setToast({ type, msg });
    setTimeout(() => setToast({ type: "", msg: "" }), 3200);
  };

  // -------------------------
  // Reset / close workflow modal
  // -------------------------
  const resetWorkflow = React.useCallback(() => {
    setStep(1);
    setSelectedBenef(null);
    setSelectedMotif(null);
    setMotifs([]);
    setSelectedMed(null);
    setSelectedSlot(null);
    setCreneaux([]);
    setWfError("");
    setBenefQuery("");
    setMonthCursor(startOfMonth(new Date()));
    setSelectedDayISO(null);
    setSuccessRecap(null);
  }, [startOfMonth]);

  const closeModal = React.useCallback(() => {
    setOpen(false);
    resetWorkflow();
  }, [resetWorkflow]);

  // -------------------------
  // Fetch RDVs
  // -------------------------
  const fetchRdvs = React.useCallback(
    async (overridePage) => {
      setLoading(true);
      setError("");

      const nextPage = overridePage ?? page;

      try {
        const params = new URLSearchParams();
        params.set("page", String(nextPage));
        params.set("limit", String(limit));
        if (qRef.current.trim()) params.set("q", qRef.current.trim());

        const res = await fetch(`${API_BASE}/collectivite/rendezvous?${params.toString()}`, {
          headers: authHeaders,
        });

        const data = await res.json().catch(() => ({}));

        if (!res.ok) {
          setError(data.msg || "Erreur lors du chargement des rendez-vous.");
          setRdvs([]);
          setTotal(0);
          return;
        }

        setRdvs(data.items || []);
        setTotal(data.total || 0);
      } catch {
        setError("Impossible de contacter le serveur.");
        setRdvs([]);
        setTotal(0);
      } finally {
        setLoading(false);
      }
    },
    [authHeaders, page]
  );

  React.useEffect(() => {
    fetchRdvs();
  }, [fetchRdvs, page]);

  // Recherche automatique avec debounce (300ms)
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      fetchRdvs(1);
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line
  }, [q]);

  // Auto-scroll la pill active dans le conteneur horizontal (mobile)
  React.useEffect(() => {
    const container = document.querySelector('.rdv2Steps');
    const activeEl = container?.querySelector('.rdv2StepPill.active');
    if (activeEl) {
      activeEl.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  }, [step]);

  // -------------------------
  // Fetch bénéficiaires
  // -------------------------
  const fetchBeneficiaires = React.useCallback(async () => {
    setWfLoading(true);
    setWfError("");
    try {
      const res = await fetch(`${API_BASE}/collectivite/beneficiaires?page=1&limit=200`, {
        headers: authHeaders,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) setWfError(data.msg || "Erreur chargement bénéficiaires");
      else setBeneficiaires((data.items || []).filter((b) => b.statut === "ACTIF"));
    } catch {
      setWfError("Impossible de contacter le serveur.");
    } finally {
      setWfLoading(false);
    }
  }, [authHeaders]);

  // -------------------------
  // Fetch motifs
  // -------------------------
  const fetchMotifs = React.useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/collectivite/motifs`, { headers: authHeaders });
      const data = await res.json().catch(() => ({}));
      if (res.ok) setMotifs(data.items || []);
    } catch {
      // silencieux, la liste reste vide
    }
  }, [authHeaders]);

  // -------------------------
  // Fetch médecins
  // -------------------------
  const fetchMedecins = React.useCallback(async () => {
    setWfLoading(true);
    setWfError("");
    try {
      const res = await fetch(`${API_BASE}/collectivite/medecins`, { headers: authHeaders });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setWfError(data.msg || "Erreur chargement médecins");
        setMedecins([]);
        return;
      }
      const items = data.items || data.medecins || [];
      setMedecins(items);
    } catch {
      setWfError("Impossible de contacter le serveur.");
      setMedecins([]);
    } finally {
      setWfLoading(false);
    }
  }, [authHeaders]);

  // -------------------------
  // Fetch créneaux
  // -------------------------
  const fetchCreneaux = React.useCallback(
    async (medecinUserId) => {
      if (!medecinUserId) {
        setWfError("Médecin invalide.");
        setCreneaux([]);
        return;
      }

      setWfLoading(true);
      setWfError("");

      try {
        const d1 = new Date();
        d1.setHours(0, 0, 0, 0);

        const d2 = new Date();
        d2.setDate(d2.getDate() + 120);
        d2.setHours(23, 59, 59, 999);

        const params = new URLSearchParams();
        params.set("medecin_user_id", String(medecinUserId));
        params.set("date_debut", toISODate(d1));
        params.set("date_fin", toISODate(d2));

        const url = `${API_BASE}/collectivite/creneaux-disponibles?${params.toString()}`;

        const res = await fetch(url, { headers: authHeaders });
        const data = await res.json().catch(() => ({}));

        if (!res.ok) {
          setWfError(data.msg || "Erreur chargement créneaux");
          setCreneaux([]);
          return;
        }

        const raw = data.items || data.creneaux || data.data || [];
        const items = (Array.isArray(raw) ? raw : [])
          .map((c) => ({
            ...c,
            start: c.start || c.debut,
            end: c.end || c.fin,
          }))
          .filter((c) => c.start && c.end)
          .sort((a, b) => new Date(a.start) - new Date(b.start));

        setCreneaux(items);

        if (items.length === 0) {
          setWfError("Aucun créneau LIBRE trouvé pour ce médecin (sur 120 jours).");
          return;
        }

        const firstDayISO = toISODate(new Date(items[0].start));
        setSelectedDayISO(firstDayISO);

        const d = new Date(firstDayISO);
        setMonthCursor(startOfMonth(d));
      } catch {
        setWfError("Impossible de contacter le serveur.");
        setCreneaux([]);
      } finally {
        setWfLoading(false);
      }
    },
    [authHeaders, toISODate, startOfMonth]
  );

  // -------------------------
  // Modifier RDV — fetch créneaux
  // -------------------------
  const fetchModifCreneaux = React.useCallback(
    async (medecinUserId) => {
      setModifLoading(true);
      setModifError("");
      setModifCreneaux([]);
      setModifSelectedDay(null);
      setModifSelectedSlot(null);

      try {
        const d1 = new Date();
        d1.setHours(0, 0, 0, 0);
        const d2 = new Date();
        d2.setDate(d2.getDate() + 120);
        d2.setHours(23, 59, 59, 999);

        const params = new URLSearchParams();
        params.set("medecin_user_id", String(medecinUserId));
        params.set("date_debut", toISODate(d1));
        params.set("date_fin", toISODate(d2));

        const res = await fetch(
          `${API_BASE}/collectivite/creneaux-disponibles?${params.toString()}`,
          { headers: authHeaders }
        );
        const data = await res.json().catch(() => ({}));

        if (!res.ok) {
          setModifError(data.msg || "Erreur chargement créneaux");
          return;
        }

        const raw = data.items || data.creneaux || data.data || [];
        const items = (Array.isArray(raw) ? raw : [])
          .map((c) => ({ ...c, start: c.start || c.debut, end: c.end || c.fin }))
          .filter((c) => c.start && c.end)
          .sort((a, b) => new Date(a.start) - new Date(b.start));

        setModifCreneaux(items);

        if (items.length === 0) {
          setModifError("Aucun créneau disponible pour ce médecin (sur 120 jours).");
          return;
        }

        const firstDayISO = toISODate(new Date(items[0].start));
        setModifSelectedDay(firstDayISO);
        setModifMonthCursor(startOfMonth(new Date(firstDayISO)));
      } catch {
        setModifError("Impossible de contacter le serveur.");
      } finally {
        setModifLoading(false);
      }
    },
    [authHeaders, toISODate, startOfMonth]
  );

  const submitModif = React.useCallback(async () => {
    if (!modifModal || !modifSelectedSlot) return;
    setModifLoading(true);
    setModifError("");

    try {
      const res = await fetch(
        `${API_BASE}/collectivite/rendezvous/${modifModal.id}/modifier`,
        {
          method: "PATCH",
          headers: authHeaders,
          body: JSON.stringify({ creneau_id: modifSelectedSlot.id }),
        }
      );
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setModifError(data.msg || "Erreur lors de la modification");
        return;
      }

      setModifModal(null);
      setModifCreneaux([]);
      setModifSelectedSlot(null);
      showToast("success", "Rendez-vous reprogrammé avec succès");
      fetchRdvs();
    } catch {
      setModifError("Impossible de contacter le serveur.");
    } finally {
      setModifLoading(false);
    }
  }, [modifModal, modifSelectedSlot, authHeaders, fetchRdvs]);

  // Dérivés pour le modal de modification
  const modifSlotsByDate = React.useMemo(() => {
    const map = new Map();
    for (const c of modifCreneaux) {
      const key = toISODate(new Date(c.start));
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(c);
    }
    return map;
  }, [modifCreneaux, toISODate]);

  const modifMonthDays = React.useMemo(() => {
    if (!modifMonthCursor) return [];
    const start = startOfMonth(modifMonthCursor);
    const end = endOfMonth(modifMonthCursor);
    const days = [];
    const cursor = new Date(start);
    while (cursor <= end) {
      days.push(new Date(cursor));
      cursor.setDate(cursor.getDate() + 1);
    }
    return days;
  }, [modifMonthCursor, startOfMonth, endOfMonth]);

  const modifVisibleDays = React.useMemo(() => {
    const start = new Date(); start.setHours(0, 0, 0, 0);
    const end = new Date(); end.setDate(end.getDate() + 120); end.setHours(23, 59, 59, 999);
    return modifMonthDays.filter((d) => d >= start && d <= end);
  }, [modifMonthDays]);

  const modifDaySlots = React.useMemo(() => {
    if (!modifSelectedDay) return [];
    return modifSlotsByDate.get(modifSelectedDay) || [];
  }, [modifSlotsByDate, modifSelectedDay]);

  const modifSelectedDayObj = React.useMemo(() => {
    if (!modifSelectedDay) return null;
    const d = new Date(modifSelectedDay);
    return Number.isNaN(d.getTime()) ? null : d;
  }, [modifSelectedDay]);

  const modifMonthTitle = React.useMemo(
    () => (modifMonthCursor ? monthLabel(modifMonthCursor) : ""),
    [monthLabel, modifMonthCursor]
  );

  const modifCanPrevMonth = React.useMemo(() => {
    if (!modifMonthCursor) return false;
    const start = new Date(); start.setHours(0, 0, 0, 0);
    return endOfMonth(addMonths(modifMonthCursor, -1)) >= start;
  }, [modifMonthCursor, endOfMonth, addMonths]);

  const modifCanNextMonth = React.useMemo(() => {
    if (!modifMonthCursor) return false;
    const end = new Date(); end.setDate(end.getDate() + 120); end.setHours(23, 59, 59, 999);
    return startOfMonth(addMonths(modifMonthCursor, 1)) <= end;
  }, [modifMonthCursor, startOfMonth, addMonths]);

  // -------------------------
  // Open workflow modal
  // -------------------------
  const openModal = React.useCallback(async () => {
    setOpen(true);
    resetWorkflow();
    await Promise.all([fetchBeneficiaires(), fetchMotifs(), fetchMedecins()]);
  }, [fetchBeneficiaires, fetchMotifs, fetchMedecins, resetWorkflow]);

  // -------------------------
  // Create RDV (confirm)
  // -------------------------
  const confirmRdv = React.useCallback(async () => {
    if (!selectedBenef?.id || !selectedMed?.user_id || !selectedSlot?.id) {
      setWfError("Sélection incomplète (bénéficiaire / médecin / créneau).");
      return;
    }

    setWfLoading(true);
    setWfError("");

    try {
      const res = await fetch(`${API_BASE}/collectivite/rendezvous`, {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify({
          beneficiaire_id: selectedBenef.id,
          medecin_user_id: selectedMed.user_id,
          creneau_id: selectedSlot.id,
          motif_id: selectedMotif?.id,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setWfError(data.msg || "Erreur lors de la création du rendez-vous.");
        return;
      }

      setSuccessRecap({
        reference: data?.item?.reference,
        beneficiaire: selectedBenef,
        motif: selectedMotif,
        medecin: selectedMed,
        slot: selectedSlot,
      });

      setStep(6);
      await fetchRdvs();
    } catch {
      setWfError("Impossible de contacter le serveur.");
    } finally {
      setWfLoading(false);
    }
  }, [authHeaders, fetchRdvs, selectedBenef, selectedMotif, selectedMed, selectedSlot]);

  // -------------------------
  // Cancel RDV (submit)
  // -------------------------
  const submitCancel = React.useCallback(async () => {
    if (!cancelModal?.id || !cancelModal?.motif) return;

    setWfLoading(true);
    setWfError("");

    try {
      const res = await fetch(`${API_BASE}/collectivite/rendezvous/${cancelModal.id}/annuler`, {
        method: "PATCH",
        headers: authHeaders,
        body: JSON.stringify({ motif_annulation: cancelModal.motif }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        showToast("error", data.msg || "Erreur lors de l'annulation");
        return;
      }

      setConfirmCancel(false);
      setCancelModal(null);
      showToast("success", "Rendez-vous annulé avec succès");
      await fetchRdvs();
    } catch {
      showToast("error", "Impossible de contacter le serveur.");
    } finally {
      setWfLoading(false);
    }
  }, [authHeaders, cancelModal, fetchRdvs]);

  // -------------------------
  // Computed
  // -------------------------
  const canGo2 = Boolean(selectedBenef);
  const canGo3 = Boolean(selectedBenef && selectedMotif);
  const canGo4 = Boolean(selectedBenef && selectedMotif && selectedMed);
  const canGo5 = Boolean(selectedBenef && selectedMotif && selectedMed && selectedSlot);

  const filteredBeneficiaires = React.useMemo(() => {
    const qx = benefQuery.trim().toLowerCase();
    const list = beneficiaires || [];
    if (!qx) return list;
    return list.filter((b) => {
      const code = String(b.code || "").toLowerCase();
      const email = String(b.email || "").toLowerCase();
      const nom = String(b.nom || "").toLowerCase();
      const prenom = String(b.prenom || "").toLowerCase();
      return code.includes(qx) || email.includes(qx) || nom.includes(qx) || prenom.includes(qx);
    });
  }, [benefQuery, beneficiaires]);

  const slotsByDate = React.useMemo(() => {
    const map = new Map();
    for (const c of creneaux) {
      const key = toISODate(new Date(c.start));
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(c);
    }
    for (const [k, arr] of map.entries()) {
      arr.sort((a, b) => new Date(a.start) - new Date(b.start));
      map.set(k, arr);
    }
    return map;
  }, [creneaux, toISODate]);

  const range120 = React.useMemo(() => {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    const end = new Date();
    end.setDate(end.getDate() + 120);
    end.setHours(23, 59, 59, 999);
    return { start, end };
  }, []);

  const isIn120Days = React.useCallback(
    (d) => d >= range120.start && d <= range120.end,
    [range120]
  );

  const monthDays = React.useMemo(() => {
    const start = startOfMonth(monthCursor);
    const end = endOfMonth(monthCursor);
    const days = [];
    const cursor = new Date(start);

    while (cursor <= end) {
      days.push(new Date(cursor));
      cursor.setDate(cursor.getDate() + 1);
    }

    return days;
  }, [monthCursor, startOfMonth, endOfMonth]);

  const visibleMonthDays = React.useMemo(() => {
    return monthDays.filter((d) => isIn120Days(d));
  }, [monthDays, isIn120Days]);

  const monthTitle = React.useMemo(() => monthLabel(monthCursor), [monthLabel, monthCursor]);

  const canGoPrevMonth = React.useMemo(() => {
    const prevMonth = addMonths(monthCursor, -1);
    return endOfMonth(prevMonth) >= range120.start;
  }, [monthCursor, addMonths, endOfMonth, range120]);

  const canGoNextMonth = React.useMemo(() => {
    const nextMonth = addMonths(monthCursor, 1);
    return startOfMonth(nextMonth) <= range120.end;
  }, [monthCursor, addMonths, startOfMonth, range120]);

  const selectedDay = React.useMemo(() => {
    if (!selectedDayISO) return null;
    const d = new Date(selectedDayISO);
    if (Number.isNaN(d.getTime())) return null;
    return d;
  }, [selectedDayISO]);

  const daySlots = React.useMemo(() => {
    if (!selectedDayISO) return [];
    return slotsByDate.get(selectedDayISO) || [];
  }, [slotsByDate, selectedDayISO]);

  const benefInitials = (b) => {
    const p = (b?.prenom || "").trim();
    const n = (b?.nom || "").trim();
    const i1 = p ? p[0] : "";
    const i2 = n ? n[0] : "";
    const initials = (i1 + i2).toUpperCase();
    return initials || "??";
  };

  const goToPreviousMonth = React.useCallback(() => {
    if (!canGoPrevMonth) return;

    const prevMonth = addMonths(monthCursor, -1);
    setMonthCursor(prevMonth);
    setSelectedSlot(null);

    const prevDays = [];
    const start = startOfMonth(prevMonth);
    const end = endOfMonth(prevMonth);
    const cursor = new Date(start);

    while (cursor <= end) {
      if (isIn120Days(cursor)) prevDays.push(new Date(cursor));
      cursor.setDate(cursor.getDate() + 1);
    }

    const firstAvailable = prevDays.find((d) => slotsByDate.has(toISODate(d)));
    if (firstAvailable) setSelectedDayISO(toISODate(firstAvailable));
    else if (prevDays.length > 0) setSelectedDayISO(toISODate(prevDays[0]));
    else setSelectedDayISO(null);
  }, [
    canGoPrevMonth,
    addMonths,
    monthCursor,
    startOfMonth,
    endOfMonth,
    isIn120Days,
    slotsByDate,
    toISODate,
  ]);

  const goToNextMonth = React.useCallback(() => {
    if (!canGoNextMonth) return;

    const nextMonth = addMonths(monthCursor, 1);
    setMonthCursor(nextMonth);
    setSelectedSlot(null);

    const nextDays = [];
    const start = startOfMonth(nextMonth);
    const end = endOfMonth(nextMonth);
    const cursor = new Date(start);

    while (cursor <= end) {
      if (isIn120Days(cursor)) nextDays.push(new Date(cursor));
      cursor.setDate(cursor.getDate() + 1);
    }

    const firstAvailable = nextDays.find((d) => slotsByDate.has(toISODate(d)));
    if (firstAvailable) setSelectedDayISO(toISODate(firstAvailable));
    else if (nextDays.length > 0) setSelectedDayISO(toISODate(nextDays[0]));
    else setSelectedDayISO(null);
  }, [
    canGoNextMonth,
    addMonths,
    monthCursor,
    startOfMonth,
    endOfMonth,
    isIn120Days,
    slotsByDate,
    toISODate,
  ]);

  const MODAL_STEPS = [
    { n: 1, label: "Bénéficiaire" },
    { n: 2, label: "Motif" },
    { n: 3, label: "Médecin" },
    { n: 4, label: "Créneau" },
    { n: 5, label: "Confirmation" },
  ];

  const HorizStepper = () => (
    <div className="rdvPageStepper">
      {MODAL_STEPS.map(({ n, label }, i) => (
        <React.Fragment key={n}>
          <div className={`rdvPageStep ${step === n ? "active" : step > n ? "done" : ""}`}>
            <div className="rdvPageStepBubble">{step > n ? "✓" : n}</div>
            <div className="rdvPageStepLabel">{label}</div>
          </div>
          {i < MODAL_STEPS.length - 1 && (
            <div className={`rdvPageStepLine ${step > n ? "done" : ""}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  const StepPill = ({ n, label }) => {
    const active = step === n;
    const done = step > n;

    const canNavigate =
      n <= step ||
      (n === 2 && canGo2) ||
      (n === 3 && canGo3) ||
      (n === 4 && canGo4) ||
      (n === 5 && canGo5);

    return (
      <button
        type="button"
        className={`rdv2StepPill ${active ? "active" : ""} ${done ? "done" : ""} ${!canNavigate ? "locked" : ""}`}
        onClick={() => {
          if (!canNavigate) return;
          if (n <= step) { setStep(n); return; }
          if (n === 2 && canGo2) setStep(2);
          else if (n === 3 && canGo3) setStep(3);
          else if (n === 4 && canGo4) setStep(4);
          else if (n === 5 && canGo5) setStep(5);
        }}
      >
        <span className="rdv2StepDot">{done ? "✓" : n}</span>
        <span className="rdv2StepLabel">{label}</span>
      </button>
    );
  };

  // -------------------------
  // UI
  // -------------------------
  return (
    <div className="adminPanel">
      <div className="adminPanelHeader">
        <div></div>

        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <button
            className="btnSecondary"
            onClick={async () => {
              setQ("");
              setPage(1);
              await fetchRdvs(1);
              showToast("success", "Les rendez-vous sont à jour");
            }}
            disabled={loading}
          >
            {loading ? "Chargement..." : "Rafraîchir"}
          </button>
          <button className="btnPrimary" onClick={openModal}>
            + Ajouter RDV
          </button>
        </div>
      </div>

      <div className="adminToolbar">
        <input
          className="adminInput"
          placeholder="Rechercher par nom de bénéficiaire…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>

      {error && <div className="adminError">{error}</div>}

      <div className="adminTableWrap">
        <table className="adminTable">
          <thead>
            <tr>
              <th>Référence</th>
              <th>Bénéficiaire</th>
              <th>Médecin</th>
              <th>Date</th>
              <th>Statut</th>
              <th style={{ width: 260 }}>Actions</th>
            </tr>
          </thead>

          <tbody>
            {!loading && rdvs.length === 0 && (
              <tr>
                <td colSpan={7} className="adminMonoo">
                  Aucun rendez-vous pour le moment.
                </td>
              </tr>
            )}

            {rdvs.map((r) => (
              <tr key={r.id}>
                <td className="adminMono">{r.reference || "—"}</td>
                <td className="adminMono">
                  {r.beneficiaire_prenom && r.beneficiaire_nom
                    ? `${r.beneficiaire_prenom} ${r.beneficiaire_nom}`
                    : r.beneficiaire_code || "—"}
                </td>
                <td className="adminMono">Dr. {r.medecin_nom || "—"}</td>
                <td className="adminMono">{fmtDateTime(r.debut)}</td>
                <td>
                  <span className={`statusBadge ${r.statut === "CONFIRME" ? "on" : "off"}`}>
                    {r.statut || "—"}
                  </span>
                </td>
                <td style={{ display: "flex", gap: 6, flexWrap: "nowrap", alignItems: "center" }}>
                  <button
                    className="editButton"
                    onClick={() => setDetailModal(r)}
                  >
                    Détails
                  </button>
                  <button
                    className="editButtonOrange"
                    disabled={r.statut === "ANNULE"}
                    onClick={async () => {
                      setModifModal({
                        id: r.id,
                        reference: r.reference,
                        medecin_nom: r.medecin_nom,
                        medecin_user_id: r.medecin_user_id,
                        debut: r.debut,
                        beneficiaire_prenom: r.beneficiaire_prenom,
                        beneficiaire_nom: r.beneficiaire_nom,
                      });
                      await fetchModifCreneaux(r.medecin_user_id);
                    }}
                  >
                    Modifier
                  </button>
                  <button
                    className="btnDangerOutline"
                    disabled={r.statut === "ANNULE"}
                    onClick={() => {
                      setConfirmCancel(false);
                      setCancelModal({
                        id: r.id,
                        reference: r.reference,
                        medecin_nom: r.medecin_nom,
                        debut: r.debut,
                        motif: MOTIFS_ANNULATION[0] || "",
                      });
                    }}
                  >
                    {r.statut === "ANNULE" ? "Annulé" : "Annuler"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="adminPager">
        <button
          className="btnSecondary"
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          disabled={page === 1}
        >
          ←
        </button>

        <div className="adminPagerInfo">
          Page <b>{page}</b> / {pages} — Total: <b>{total}</b>
        </div>

        <button
          className="btnSecondary"
          onClick={() => setPage((p) => Math.min(pages, p + 1))}
          disabled={page >= pages}
        >
          →
        </button>
      </div>

      {/* =========================
          MODAL (création RDV)
         ========================= */}
      {open && (
        <div className="rdv2Overlay" role="dialog" aria-modal="true">
          <div className="rdv2Modal">
            <div className="rdv2Header">
              <div className="rdv2HeaderLeft">
                <div className="rdv2Title">Nouveau rendez-vous</div>
                <div className="rdv2Sub"></div>
              </div>

              <div className="rdv2HeaderRight">
                <button className="btnSecondary" onClick={closeModal}>
                  Fermer
                </button>
              </div>
            </div>

            {step < 6 && <HorizStepper />}

            {wfError && <div className="adminError rdv2Err">{wfError}</div>}

            <div className="rdv2Body">
              <div className="rdv2Left">
                <div className="rdv2Summary">
                  <div className="rdv2SummaryTitle">Sélection</div>

                  <div className="rdv2SumRow">
                    <div className="rdv2SumLabel">Bénéficiaire</div>
                    <div className="rdv2SumValue">
                      {selectedBenef ? (
                        `${selectedBenef.code} — ${selectedBenef.email}`
                      ) : (
                        <span className="rdv2Muted">Non sélectionné</span>
                      )}
                    </div>
                    {selectedBenef && step > 1 && (
                      <button className="rdv2LinkBtn" type="button" onClick={() => setStep(1)}>
                        Modifier
                      </button>
                    )}
                  </div>

                  <div className="rdv2SumRow">
                    <div className="rdv2SumLabel">Motif</div>
                    <div className="rdv2SumValue">
                      {selectedMotif ? selectedMotif.libelle : <span className="rdv2Muted">Non sélectionné</span>}
                    </div>
                    {selectedMotif && step > 2 && (
                      <button className="rdv2LinkBtn" type="button" onClick={() => setStep(2)}>
                        Modifier
                      </button>
                    )}
                  </div>

                  <div className="rdv2SumRow">
                    <div className="rdv2SumLabel">Médecin</div>
                    <div className="rdv2SumValue">
                      {selectedMed ? (
                        <>
                          {formatDoctorName(selectedMed)}
                          <div className="rdv2SumSub">{doctorAddressLine(selectedMed)}</div>
                        </>
                      ) : (
                        <span className="rdv2Muted">Non sélectionné</span>
                      )}
                    </div>
                    {selectedMed && step > 3 && (
                      <button
                        className="rdv2LinkBtn"
                        type="button"
                        onClick={() => {
                          setSelectedMed(null);
                          setSelectedSlot(null);
                          setCreneaux([]);
                          setSelectedDayISO(null);
                          setMonthCursor(startOfMonth(new Date()));
                          setStep(3);
                        }}
                      >
                        Modifier
                      </button>
                    )}
                  </div>

                  <div className="rdv2SumRow">
                    <div className="rdv2SumLabel">Créneau</div>
                    <div className="rdv2SumValue">
                      {selectedSlot ? (
                        `${fmtDateTime(selectedSlot.start)}`
                      ) : (
                        <span className="rdv2Muted">Non sélectionné</span>
                      )}
                    </div>
                    {selectedSlot && step > 4 && (
                      <button
                        className="rdv2LinkBtn"
                        type="button"
                        onClick={() => {
                          setSelectedSlot(null);
                          setStep(4);
                        }}
                      >
                        Modifier
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className="rdv2Right">
                {/* STEP 1 */}
                {step === 1 && (
                  <div className="rdv2Card">
                    <div className="rdv2CardHead">
                      <div>
                        <div className="rdv2CardTitle">Choisir un bénéficiaire</div>
                        <div className="rdv2CardSub">Recherche rapide + sélection.</div>
                      </div>
                      <button className="btnPrimary" disabled={!canGo2} onClick={() => setStep(2)}>
                        Continuer
                      </button>
                    </div>

                    <div className="rdv2SearchRow">
                      <input
                        className="adminInput"
                        placeholder="Rechercher (code, email, nom, prénom)…"
                        value={benefQuery}
                        onChange={(e) => setBenefQuery(e.target.value)}
                      />
                      <div className="rdv2Hint">{filteredBeneficiaires.length} résultat(s)</div>
                    </div>

                    <div className="rdv2List">
                      {wfLoading ? (
                        <div className="adminPlaceholder">
                          <div className="adminPlaceholderTitle">Chargement…</div>
                        </div>
                      ) : filteredBeneficiaires.length === 0 ? (
                        <div className="adminPlaceholder">
                          <div className="adminPlaceholderTitle">Aucun bénéficiaire</div>
                        </div>
                      ) : (
                        filteredBeneficiaires.map((b) => {
                          const active = String(selectedBenef?.id) === String(b.id);
                          return (
                            <button
                              key={b.id}
                              type="button"
                              className={`rdv2Row ${active ? "active" : ""}`}
                              onClick={() => setSelectedBenef(b)}
                            >
                              <div className="rdv2Avatar">{benefInitials(b)}</div>
                              <div className="rdv2RowMain">
                                <div className="rdv2RowTop">
                                  <span className="rdv2Pill">{b.code}</span>
                                  <span className="rdv2Email">{b.email}</span>
                                </div>
                                <div className="rdv2RowSub">
                                  {(b.prenom || "") + " " + (b.nom || "")}
                                </div>
                              </div>
                              <div className="rdv2RowRight">{active ? "✓" : ""}</div>
                            </button>
                          );
                        })
                      )}
                    </div>
                  </div>
                )}

                {/* STEP 2 — Motif */}
                {step === 2 && (
                  <div className="rdv2Card">
                    <div className="rdv2CardHead">
                      <div>
                        <div className="rdv2CardTitle">Motif de consultation</div>
                        <div className="rdv2CardSub">Sélectionnez le motif de la visite.</div>
                      </div>
                      <button className="btnPrimary" disabled={!canGo3} onClick={() => setStep(3)}>
                        Continuer
                      </button>
                    </div>

                    <div style={{ marginTop: 16 }}>
                      <select
                        className="adminInput"
                        value={selectedMotif?.id || ""}
                        onChange={(e) => {
                          const found = motifs.find((m) => String(m.id) === e.target.value);
                          setSelectedMotif(found || null);
                        }}
                        style={{ height: 44 }}
                      >
                        <option value="">— Choisir un motif —</option>
                        {["ADMINISTRATIONS", "USAGERS"].map((cat) => {
                          const groupe = motifs.filter((m) => m.categorie === cat);
                          if (!groupe.length) return null;
                          const label = cat === "ADMINISTRATIONS"
                            ? "Motifs médico-administratifs – Administrations"
                            : "Motifs médico-administratifs – Usagers";
                          return (
                            <optgroup key={cat} label={label}>
                              {groupe.map((m) => (
                                <option key={m.id} value={m.id}>{m.libelle}</option>
                              ))}
                            </optgroup>
                          );
                        })}
                      </select>
                    </div>

                    <div className="rdv2BottomBar">
                      <button className="btnSecondary" onClick={() => setStep(1)}>
                        ← Retour
                      </button>
                    </div>
                  </div>
                )}

                {/* STEP 3 — Médecin */}
                {step === 3 && (
                  <div className="rdv2Card">
                    <div className="rdv2CardHead">
                      <div>
                        <div className="rdv2CardTitle">Choisir un médecin</div>
                        <div className="rdv2CardSub">Sélection → chargement des créneaux.</div>
                      </div>
                      <button className="btnPrimary" disabled={!canGo4} onClick={() => setStep(4)}>
                        Continuer
                      </button>
                    </div>

                    {wfLoading ? (
                      <div className="adminPlaceholder">
                        <div className="adminPlaceholderTitle">Chargement…</div>
                      </div>
                    ) : medecins.length === 0 ? (
                      <div className="adminPlaceholder">
                        <div className="adminPlaceholderTitle">Aucun médecin</div>
                        <ul className="adminList">
                          <li>Vérifie GET /collectivite/medecins</li>
                        </ul>
                      </div>
                    ) : (
                      <div className="rdv2Doctors">
                        {medecins.map((m) => {
                          const active = String(selectedMed?.user_id) === String(m.user_id);
                          return (
                            <button
                              key={m.user_id}
                              type="button"
                              className={`rdv2DoctorRow ${active ? "active" : ""}`}
                              onClick={async () => {
                                setSelectedMed(m);
                                setSelectedSlot(null);
                                setSelectedDayISO(null);
                                await fetchCreneaux(m.user_id);
                                setStep(4);
                              }}
                            >
                              <DoctorAvatar med={m} />

                              <div className="rdv2DoctorMain">
                                <div className="rdv2DoctorName">{formatDoctorName(m)}</div>

                                <div className="rdv2DoctorMeta">
                                  <span>{m.specialite || "Spécialité —"}</span>
                                  <span className="rdv2Dot">•</span>
                                  <span>{m.agrement || "Agrément —"}</span>
                                </div>

                                <div className="rdv2DoctorAddr">{doctorAddressLine(m)}</div>
                              </div>

                              <div className="rdv2Chevron">›</div>
                            </button>
                          );
                        })}
                      </div>
                    )}

                    <div className="rdv2BottomBar">
                      <button className="btnSecondary" onClick={() => setStep(2)}>
                        ← Retour
                      </button>
                    </div>
                  </div>
                )}

                {/* STEP 4 — Créneau */}
                {step === 4 && (
                  <div className="rdv2Card">
                    <div className="rdv2CardHead">
                      <div>
                        <div className="rdv2CardTitle">Choisir un créneau</div>
                        <div className="rdv2CardSub">
                          Navigation par mois + créneaux du jour sélectionné.
                        </div>
                      </div>
                      <button className="btnPrimary" disabled={!canGo5} onClick={() => setStep(5)}>
                        Continuer
                      </button>
                    </div>

                    {!selectedMed ? (
                      <div className="adminPlaceholder">
                        <div className="adminPlaceholderTitle">Choisis un médecin d'abord</div>
                      </div>
                    ) : wfLoading ? (
                      <div className="adminPlaceholder">
                        <div className="adminPlaceholderTitle">Chargement des créneaux…</div>
                      </div>
                    ) : (
                      <div className="rdv2CalendarWrap">
                        <div className="rdv2CalHeader">
                          <button
                            className="rdv2CalNav"
                            type="button"
                            onClick={goToPreviousMonth}
                            aria-label="Mois précédent"
                            disabled={!canGoPrevMonth}
                          >
                            ‹
                          </button>

                          <div className="rdv2CalTitle">{monthTitle}</div>

                          <button
                            className="rdv2CalNav"
                            type="button"
                            onClick={goToNextMonth}
                            aria-label="Mois suivant"
                            disabled={!canGoNextMonth}
                          >
                            ›
                          </button>
                        </div>

                        <div className="rdv2DayTabs">
                          {visibleMonthDays.map((d) => {
                            const iso = toISODate(d);
                            const hasSlots = slotsByDate.has(iso);
                            const isActive = selectedDayISO === iso;

                            return (
                              <button
                                key={iso}
                                type="button"
                                className={`rdv2DayTab ${isActive ? "active" : ""}`}
                                onClick={() => {
                                  setSelectedDayISO(iso);
                                  setSelectedSlot(null);
                                }}
                                title={dayLong(d)}
                              >
                                <div className="rdv2DayName">{dayShort(d)}</div>
                                <div className="rdv2DayNum">{d.getDate()}</div>
                                <div className={`rdv2DayDot ${hasSlots ? "on" : "off"}`} />
                              </button>
                            );
                          })}
                        </div>

                        <div className="rdv2SlotsPanel">
                          <div className="rdv2SlotsHead">
                            <div className="rdv2SlotsTitle">
                              {selectedDay ? dayLong(selectedDay) : "Sélectionne un jour"}
                            </div>
                            <div className="rdv2SlotsHint">
                              {selectedDayISO
                                ? `${(slotsByDate.get(selectedDayISO) || []).length} créneau(x)`
                                : ""}
                            </div>
                          </div>

                          {selectedDayISO && daySlots.length === 0 ? (
                            <div className="rdv2Empty">Aucun créneau ce jour-là.</div>
                          ) : (
                            <div className="rdv2SlotsGrid">
                              {(daySlots || []).map((c) => {
                                const active = selectedSlot?.id === c.id;
                                return (
                                  <button
                                    key={c.id}
                                    type="button"
                                    className={`rdv2SlotBtn ${active ? "active" : ""}`}
                                    onClick={() => setSelectedSlot(c)}
                                    title={`${fmtDateTime(c.start)} → ${fmtDateTime(c.end)}`}
                                  >
                                    <span className="rdv2SlotTime">{fmtTime(c.start)}</span>
                                    <span className="rdv2SlotDur">
                                      {fmtTime(c.start)} → {fmtTime(c.end)}
                                    </span>
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    <div className="rdv2BottomBar">
                      <button className="btnSecondary" onClick={() => setStep(3)}>
                        ← Retour
                      </button>
                    </div>
                  </div>
                )}

                {/* STEP 5 — Confirmation */}
                {step === 5 && (
                  <div className="rdv2Card">
                    <div className="rdv2CardHead">
                      <div>
                        <div className="rdv2CardTitle">Confirmation</div>
                        <div className="rdv2CardSub">Vérifie puis confirme la réservation.</div>
                      </div>
                    </div>

                    <div className="rdv2Confirm">
                      <div className="rdv2ConfirmItem">
                        <div className="rdv2ConfirmLabel">
                          <FaUser className="rdv2ConfirmIcon" />
                          Bénéficiaire
                        </div>
                        <div className="rdv2ConfirmValue">
                          {selectedBenef?.prenom} {selectedBenef?.nom}
                          <div className="rdv2ConfirmSub">
                            {selectedBenef?.code} — {selectedBenef?.email}
                          </div>
                        </div>
                      </div>

                      <div className="rdv2ConfirmItem">
                        <div className="rdv2ConfirmLabel">Motif</div>
                        <div className="rdv2ConfirmValue">{selectedMotif?.libelle || "—"}</div>
                      </div>

                      <div className="rdv2ConfirmItem">
                        <div className="rdv2ConfirmLabel">
                          <FaUserMd className="rdv2ConfirmIcon" />
                          Médecin
                        </div>
                        <div className="rdv2ConfirmValue">
                          {formatDoctorName(selectedMed)}
                          <div className="rdv2ConfirmSub">
                            {selectedMed?.specialite || "—"}
                            {selectedMed?.agrement ? ` • ${selectedMed.agrement}` : ""}
                          </div>
                          <div className="rdv2ConfirmAddress">
                            {doctorAddressLine(selectedMed)}
                          </div>
                        </div>
                      </div>

                      <div className="rdv2ConfirmItem">
                        <div className="rdv2ConfirmLabel">
                          <FaCalendarAlt className="rdv2ConfirmIcon" />
                          Créneau
                        </div>
                        <div className="rdv2ConfirmValue">
                          {fmtDateTime(selectedSlot?.start)}
                          <div className="rdv2ConfirmSub">
                            {fmtTime(selectedSlot?.start)} → {fmtTime(selectedSlot?.end)}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="rdv2ConfirmActions">
                      <button className="btnSecondary" onClick={() => setStep(4)}>
                        ← Retour
                      </button>
                      <button className="btnPrimary" onClick={confirmRdv} disabled={wfLoading}>
                        {wfLoading ? "Confirmation..." : "Confirmer"}
                      </button>
                    </div>
                  </div>
                )}

                {/* STEP 6 — Succès */}
                {step === 6 && (
                  <div className="rdv2Success">
                    <div className="rdv2SuccessCard">
                      <div className="rdv2SuccessIcon">✓</div>
                      <div className="rdv2SuccessTitle">Rendez-vous confirmé</div>
                      <div className="rdv2SuccessSub">
                        Réservation enregistrée
                        {successRecap?.reference ? (
                          <>
                            {" "}
                            — <span className="rdv2Ref">{successRecap.reference}</span>
                          </>
                        ) : null}
                      </div>

                      <div className="rdv2SuccessRecap">
                        <div className="rdv2RecRow">
                          <span>Bénéficiaire</span>
                          <b>
                            {successRecap?.beneficiaire?.prenom} {successRecap?.beneficiaire?.nom}
                          </b>
                        </div>

                        <div className="rdv2RecRow">
                          <span>Motif</span>
                          <b>{successRecap?.motif?.libelle || "—"}</b>
                        </div>

                        <div className="rdv2RecRow">
                          <span>Médecin</span>
                          <b>{formatDoctorName(successRecap?.medecin)}</b>
                        </div>

                        <div className="rdv2RecRow">
                          <span>Adresse</span>
                          <b>{doctorAddressLine(successRecap?.medecin)}</b>
                        </div>

                        <div className="rdv2RecRow">
                          <span>Créneau</span>
                          <b>
                            {fmtDateTime(successRecap?.slot?.start)} → {fmtTime(successRecap?.slot?.end)}
                          </b>
                        </div>
                      </div>

                      <div className="rdv2SuccessActions">
                        <button className="btnSecondary" onClick={closeModal}>
                          Fermer
                        </button>
                        <button className="btnPrimary" onClick={closeModal}>
                          OK
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="rdv2RightFooter">
              <button className="btnSecondary" onClick={resetWorkflow}>
                Réinitialiser
              </button>
            </div>
          </div>
        </div>
      )}

      {/* =========================
          TOAST + MODALS ANNULATION
         ========================= */}
      {toast.msg && <div className={`adminToast ${toast.type}`}>{toast.msg}</div>}

      {cancelModal && !confirmCancel && (
        <div className="logoutModalOverlay">
          <div className="logoutModal" style={{ maxWidth: 520 }}>
            <div className="logoutModalTitle">Annuler le rendez-vous</div>
            <div className="logoutModalText" style={{ marginTop: 10 }}>
              <b>{cancelModal.reference}</b> — Dr.{cancelModal.medecin_nom}
              <br />
              <br />
              {fmtDateTime(cancelModal.debut)}
            </div>

            <div style={{ marginTop: 14, display: "grid", gap: 10 }}>
              <label style={{ fontWeight: 900, color: "rgba(15,23,42,0.75)", fontSize: 12 }}>
                Motif d'annulation
              </label>

              <select
                className="adminInput"
                value={cancelModal.motif}
                onChange={(e) => setCancelModal((s) => ({ ...s, motif: e.target.value }))}
              >
                {MOTIFS_ANNULATION.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>

            <div className="logoutModalActions">
              <button
                className="btnSecondary"
                onClick={() => {
                  setCancelModal(null);
                  setConfirmCancel(false);
                }}
              >
                Fermer
              </button>
              <button className="btnDanger" onClick={() => setConfirmCancel(true)}>
                Continuer
              </button>
            </div>
          </div>
        </div>
      )}

      {detailModal && (
        <div className="logoutModalOverlay">
          <div className="rdvDetailModal">
            <div className="rdvDetailHeader">
              <div className="rdvDetailTitle">Détails du rendez-vous</div>
              <span className={`statusBadge ${detailModal.statut === "CONFIRME" ? "on" : "off"}`}>
                {detailModal.statut || "—"}
              </span>
            </div>

            <div className="rdvDetailRef">{detailModal.reference || "—"}</div>

            <div className="rdvDetailSections">

              <div className="rdvDetailSection">
                <div className="rdvDetailSectionTitle">Bénéficiaire</div>
                <div className="rdvDetailRow">
                  <span className="rdvDetailLabel">Nom</span>
                  <span className="rdvDetailValue">
                    {`${detailModal.beneficiaire_prenom || ""} ${detailModal.beneficiaire_nom || ""}`.trim() || detailModal.beneficiaire_code || "—"}
                  </span>
                </div>
                <div className="rdvDetailRow">
                  <span className="rdvDetailLabel">Email</span>
                  <span className="rdvDetailValue">{detailModal.beneficiaire_email || "—"}</span>
                </div>
                <div className="rdvDetailRow">
                  <span className="rdvDetailLabel">Téléphone</span>
                  <span className="rdvDetailValue">{detailModal.beneficiaire_telephone || "—"}</span>
                </div>
              </div>

              <div className="rdvDetailSection">
                <div className="rdvDetailSectionTitle">Rendez-vous</div>
                <div className="rdvDetailRow">
                  <span className="rdvDetailLabel">Motif</span>
                  <span className="rdvDetailValue">{detailModal.motif_libelle || "—"}</span>
                </div>
                <div className="rdvDetailRow">
                  <span className="rdvDetailLabel">Date</span>
                  <span className="rdvDetailValue">{fmtDateTime(detailModal.debut)}</span>
                </div>
                <div className="rdvDetailRow">
                  <span className="rdvDetailLabel">Fin</span>
                  <span className="rdvDetailValue">{fmtTime(detailModal.fin)}</span>
                </div>
              </div>

              <div className="rdvDetailSection">
                <div className="rdvDetailSectionTitle">Médecin</div>
                <div className="rdvDetailRow">
                  <span className="rdvDetailLabel">Nom</span>
                  <span className="rdvDetailValue">
                    {detailModal.medecin_nom ? `Dr. ${detailModal.medecin_nom}` : "—"}
                  </span>
                </div>
                <div className="rdvDetailRow">
                  <span className="rdvDetailLabel">Adresse</span>
                  <span className="rdvDetailValue">{detailModal.medecin_adresse || "—"}</span>
                </div>
              </div>

              {detailModal.motif_annulation && (
                <div className="rdvDetailSection rdvDetailSectionCancel">
                  <div className="rdvDetailSectionTitle">Annulation</div>
                  <div className="rdvDetailRow">
                    <span className="rdvDetailLabel">Motif</span>
                    <span className="rdvDetailValue">{detailModal.motif_annulation}</span>
                  </div>
                </div>
              )}

            </div>

            <div className="logoutModalActions">
              <button className="btnSecondary" onClick={() => setDetailModal(null)}>
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {cancelModal && confirmCancel && (
        <div className="logoutModalOverlay">
          <div className="logoutModal" style={{ maxWidth: 520 }}>
            <div className="logoutModalTitle">Confirmation</div>
            <div className="logoutModalText" style={{ marginTop: 10 }}>
              Confirmer l'annulation du rendez-vous <b>{cancelModal.reference}</b> ?
              <br />
              <br />
              Motif : <b>{cancelModal.motif}</b>
            </div>

            <div className="logoutModalActions">
              <button className="btnSecondary" onClick={() => setConfirmCancel(false)}>
                Retour
              </button>
              <button className="btnDanger" onClick={submitCancel} disabled={wfLoading}>
                {wfLoading ? "Annulation..." : "Confirmer l'annulation"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ========================= MODAL MODIFICATION ========================= */}
      {modifModal && (
        <div className="logoutModalOverlay">
          <div className="rdvDetailModal" style={{ maxWidth: 600, width: "95%" }}>
            <div className="rdvDetailHeader">
              <div className="rdvDetailTitle">Modifier le rendez-vous</div>
              <button
                className="btnSecondary"
                style={{ fontSize: 12, padding: "4px 10px" }}
                onClick={() => {
                  setModifModal(null);
                  setModifCreneaux([]);
                  setModifSelectedSlot(null);
                  setModifError("");
                }}
              >
                ✕ Fermer
              </button>
            </div>

            <div className="rdvDetailRef">{modifModal.reference}</div>

            <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 12 }}>
              <div style={{ fontSize: 13, color: "#5c6f91" }}>
                <b>Bénéficiaire :</b> {modifModal.beneficiaire_prenom} {modifModal.beneficiaire_nom}
              </div>
              <div style={{ fontSize: 13, color: "#5c6f91" }}>
                <b>Médecin :</b> Dr. {modifModal.medecin_nom}
              </div>
              <div style={{ fontSize: 13, color: "#5c6f91" }}>
                <b>Date actuelle :</b> {fmtDateTime(modifModal.debut)}
              </div>
            </div>

            {modifError && (
              <div className="authError" style={{ marginBottom: 12 }}>{modifError}</div>
            )}

            {modifLoading ? (
              <div className="adminPlaceholder">
                <div className="adminPlaceholderTitle">Chargement des créneaux…</div>
              </div>
            ) : modifCreneaux.length === 0 ? (
              <div className="adminPlaceholder">
                <div className="adminPlaceholderTitle">Aucun créneau disponible</div>
              </div>
            ) : (
              <div className="rdv2CalendarWrap">
                <div className="rdv2CalHeader">
                  <button
                    className="rdv2CalNav"
                    type="button"
                    onClick={() => setModifMonthCursor(addMonths(modifMonthCursor, -1))}
                    disabled={!modifCanPrevMonth}
                  >
                    ‹
                  </button>
                  <div className="rdv2CalTitle">{modifMonthTitle}</div>
                  <button
                    className="rdv2CalNav"
                    type="button"
                    onClick={() => setModifMonthCursor(addMonths(modifMonthCursor, 1))}
                    disabled={!modifCanNextMonth}
                  >
                    ›
                  </button>
                </div>

                <div className="rdv2DayTabs">
                  {modifVisibleDays.map((d) => {
                    const iso = toISODate(d);
                    const hasSlots = modifSlotsByDate.has(iso);
                    const isActive = modifSelectedDay === iso;
                    return (
                      <button
                        key={iso}
                        type="button"
                        className={`rdv2DayTab ${isActive ? "active" : ""}`}
                        onClick={() => { setModifSelectedDay(iso); setModifSelectedSlot(null); }}
                        title={dayLong(d)}
                      >
                        <div className="rdv2DayName">{dayShort(d)}</div>
                        <div className="rdv2DayNum">{d.getDate()}</div>
                        <div className={`rdv2DayDot ${hasSlots ? "on" : "off"}`} />
                      </button>
                    );
                  })}
                </div>

                <div className="rdv2SlotsPanel">
                  <div className="rdv2SlotsHead">
                    <div className="rdv2SlotsTitle">
                      {modifSelectedDayObj ? dayLong(modifSelectedDayObj) : "Sélectionne un jour"}
                    </div>
                    <div className="rdv2SlotsHint">
                      {modifSelectedDay ? `${modifDaySlots.length} créneau(x)` : ""}
                    </div>
                  </div>

                  {modifSelectedDay && modifDaySlots.length === 0 ? (
                    <div className="rdv2Empty">Aucun créneau ce jour-là.</div>
                  ) : (
                    <div className="rdv2SlotsGrid">
                      {modifDaySlots.map((c) => {
                        const active = modifSelectedSlot?.id === c.id;
                        return (
                          <button
                            key={c.id}
                            type="button"
                            className={`rdv2SlotBtn ${active ? "active" : ""}`}
                            onClick={() => setModifSelectedSlot(c)}
                          >
                            <span className="rdv2SlotTime">{fmtTime(c.start)}</span>
                            <span className="rdv2SlotDur">{fmtTime(c.start)} → {fmtTime(c.end)}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="logoutModalActions" style={{ marginTop: 16 }}>
              <button
                className="btnSecondary"
                onClick={() => {
                  setModifModal(null);
                  setModifCreneaux([]);
                  setModifSelectedSlot(null);
                  setModifError("");
                }}
              >
                Annuler
              </button>
              <button
                className="btnPrimary"
                disabled={!modifSelectedSlot || modifLoading}
                onClick={submitModif}
              >
                {modifLoading ? "Modification…" : "Confirmer la modification"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}